import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '../../../components/Icon';
import { createPopupPresentation } from '../../../popups/presentation';
import { useAppStore } from '../../state/useAppStore';

const POPUP_MARGIN = 10;
const POPUP_ANCHOR_GAP = 12;

interface PopupPosition {
  readonly left: number;
  readonly top: number;
}

export function FeaturePopup() {
  const popupRef = useRef<HTMLElement | null>(null);
  const inspectedFeature = useAppStore((state) => state.shell.inspectedFeature);
  const project = useAppStore((state) => state.project);
  const closeFeatureInspection = useAppStore((state) => state.closeFeatureInspection);
  const [position, setPosition] = useState<PopupPosition | null>(null);
  const layer = inspectedFeature
    ? project.layers.find((candidate) => candidate.id === inspectedFeature.layerId)
    : undefined;
  const presentation = useMemo(
    () =>
      inspectedFeature && layer
        ? createPopupPresentation(layer.popup, inspectedFeature.properties)
        : null,
    [inspectedFeature, layer],
  );

  const closePopup = useCallback(() => {
    const restoreMapFocus = popupRef.current?.contains(document.activeElement) ?? false;
    const mapCanvas = restoreMapFocus
      ? document.querySelector<HTMLElement>('.maplibregl-canvas')
      : null;
    closeFeatureInspection();
    if (mapCanvas) requestAnimationFrame(() => mapCanvas.focus());
  }, [closeFeatureInspection]);

  useEffect(() => {
    if (!inspectedFeature) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      closePopup();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [closePopup, inspectedFeature]);

  useLayoutEffect(() => {
    const popup = popupRef.current;
    const workspace = popup?.closest<HTMLElement>('.map-viewport');
    if (!popup || !workspace || !inspectedFeature || !presentation) return;

    const updatePosition = () => {
      const width = popup.offsetWidth;
      const height = popup.offsetHeight;
      const workspaceWidth = workspace.clientWidth;
      const workspaceHeight = workspace.clientHeight;
      const minimumLeft = POPUP_MARGIN;
      const maximumLeft = Math.max(minimumLeft, workspaceWidth - width - POPUP_MARGIN);
      const left = Math.min(
        maximumLeft,
        Math.max(minimumLeft, inspectedFeature.screenPoint.x - width / 2),
      );
      const above = inspectedFeature.screenPoint.y - height - POPUP_ANCHOR_GAP;
      const below = inspectedFeature.screenPoint.y + POPUP_ANCHOR_GAP;
      const preferredTop = above >= POPUP_MARGIN ? above : below;
      const maximumTop = Math.max(POPUP_MARGIN, workspaceHeight - height - POPUP_MARGIN);
      setPosition({
        left,
        top: Math.min(maximumTop, Math.max(POPUP_MARGIN, preferredTop)),
      });
    };

    updatePosition();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(updatePosition);
    observer.observe(workspace);
    observer.observe(popup);
    return () => observer.disconnect();
  }, [inspectedFeature, presentation]);

  if (!inspectedFeature || !layer || !presentation) return null;

  return (
    <aside
      aria-label={`Feature details for ${layer.name}`}
      className="feature-popup"
      data-feature-key={inspectedFeature.featureKey}
      ref={popupRef}
      role="dialog"
      style={{
        left: position?.left ?? inspectedFeature.screenPoint.x,
        top: position?.top ?? inspectedFeature.screenPoint.y,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      <header className="feature-popup-header">
        <div>
          <span className="feature-popup-layer">{layer.name}</span>
          {presentation.title !== null ? <h2>{presentation.title}</h2> : null}
        </div>
        <button
          aria-label="Close feature details"
          className="icon-button feature-popup-close"
          onClick={closePopup}
          title="Close"
          type="button"
        >
          <Icon name="close" />
        </button>
      </header>
      {presentation.rows.length > 0 ? (
        <dl className="feature-popup-fields">
          {presentation.rows.map((row) => (
            <div key={row.field}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </aside>
  );
}
